export const DRUM_VOICE_FLOOR = 0.0001;
export const DRUM_VOICE_GAIN = 0.72;
export const DRUM_CHOKE_FADE_SECONDS = 0.008;

const clamp = (value, min, max, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(min, Math.min(max, number)) : fallback;
};

export function drumLaneRole(row = {}, sample = {}) {
  const label = `${row?.key || ''} ${row?.name || ''} ${sample?.name || ''}`.toLowerCase();
  if (/(^|[\s_-])(808|sub|subbass|sub-bass)([\s_.-]|$)/.test(label)) return 'sub';
  if (/(^|[\s_-])(kick|bd|bassdrum|bass-drum)([\s_.-]|$)/.test(label)) return 'kick';
  return 'other';
}

export function shouldChokeDrumLane(row = {}, sample = {}, explicitValue) {
  if (typeof explicitValue === 'boolean') return explicitValue;
  const role = drumLaneRole(row, sample);
  return role === 'kick' || role === 'sub';
}

export function drumVoiceEnvelope({
  startTime = 0,
  naturalDuration = 0.1,
  noteDuration = null,
  attackMs = 2,
  releaseMs = 10,
  playbackMode = 'drum'
} = {}) {
  const start = Math.max(0, Number(startTime) || 0);
  const natural = Math.max(0.01, Number(naturalDuration) || 0.1);
  const attack = clamp(attackMs, 0.5, 30, 2) / 1000;
  const release = clamp(releaseMs, 3, 250, 10) / 1000;
  const requested = Number.isFinite(Number(noteDuration)) && Number(noteDuration) > 0
    ? Number(noteDuration)
    : natural;
  const bodyDuration = playbackMode === 'oneshot' ? Math.min(natural, requested) : natural;
  const bodyEnd = start + Math.max(0.01, bodyDuration);
  const fadeEnd = playbackMode === 'oneshot'
    ? Math.min(start + natural, bodyEnd + release)
    : bodyEnd;
  const attackEnd = Math.min(start + attack, Math.max(start + 0.0005, fadeEnd - 0.001));
  const fadeStart = Math.max(attackEnd, fadeEnd - Math.min(release, Math.max(0.001, fadeEnd - attackEnd)));
  return { start, attackEnd, fadeStart, fadeEnd, sourceStop: fadeEnd + 0.003 };
}

export function applyDrumVoiceEnvelope(audioParam, peakGain, envelope) {
  const peak = Math.max(DRUM_VOICE_FLOOR, Number(peakGain) || DRUM_VOICE_FLOOR);
  audioParam.cancelScheduledValues(envelope.start);
  audioParam.setValueAtTime(DRUM_VOICE_FLOOR, envelope.start);
  audioParam.linearRampToValueAtTime(peak, envelope.attackEnd);
  audioParam.setValueAtTime(peak, envelope.fadeStart);
  audioParam.exponentialRampToValueAtTime(DRUM_VOICE_FLOOR, envelope.fadeEnd);
}

export function placeScheduledChokeVoice(voices = [], voice) {
  const insertAt = voices.findIndex((candidate) => candidate.startTime > voice.startTime);
  const index = insertAt < 0 ? voices.length : insertAt;
  return {
    previous: voices[index - 1] || null,
    next: voices[index] || null,
    ordered: [...voices.slice(0, index), voice, ...voices.slice(index)]
  };
}

export function fadeAndStopDrumVoice(voice, atTime, fadeSeconds = DRUM_CHOKE_FADE_SECONDS) {
  if (!voice?.source) return false;
  const now = Math.max(0, Number(atTime) || 0);
  const fade = clamp(fadeSeconds, 0.003, 0.03, DRUM_CHOKE_FADE_SECONDS);
  try {
    if (Number.isFinite(voice.startTime) && voice.startTime > now + fade) {
      voice.source.stop(now);
      return true;
    }
    const gain = voice.gainNode?.gain;
    if (gain) {
      if (typeof gain.cancelAndHoldAtTime === 'function') gain.cancelAndHoldAtTime(now);
      else {
        const held = Math.max(DRUM_VOICE_FLOOR, Number(gain.value) || DRUM_VOICE_FLOOR);
        gain.cancelScheduledValues(now);
        gain.setValueAtTime(held, now);
      }
      gain.linearRampToValueAtTime(DRUM_VOICE_FLOOR, now + fade);
    }
    voice.source.stop(now + fade + 0.002);
    return true;
  } catch (_) {
    return false;
  }
}

export function configureDrumBus(ctx, destination) {
  const input = ctx.createGain();
  const dcBlocker = ctx.createBiquadFilter();
  dcBlocker.type = 'highpass';
  dcBlocker.frequency.value = 18;
  dcBlocker.Q.value = 0.707;

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -9;
  compressor.knee.value = 12;
  compressor.ratio.value = 3;
  compressor.attack.value = 0.006;
  compressor.release.value = 0.12;

  const output = ctx.createGain();
  output.gain.value = 0.82;
  input.connect(dcBlocker);
  dcBlocker.connect(compressor);
  compressor.connect(output);
  output.connect(destination);
  return { input, dcBlocker, compressor, output };
}
